import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Music, Image, Type, Video, Check, Coins, Play, Pause, SkipBack, SkipForward, Volume2 } from "lucide-react";
import { Slider } from "@/components/ui/slider";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import deadWordsLogo from "@/assets/dead-words-logo.png";
import AudioUploader from "@/components/DeadWords/AudioUploader";
import VisualsUploader from "@/components/DeadWords/VisualsUploader";
import LyricsEditor from "@/components/DeadWords/LyricsEditor";
import VideoGenerator from "@/components/DeadWords/VideoGenerator";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

// Simple synced lyrics component
function SyncedLyricsDisplay({ lyrics, currentTime, duration }) {
  const allLines = lyrics.split('\n');
  const nonEmptyLines = allLines.filter(l => l.trim());
  const timePerLine = duration > 0 && nonEmptyLines.length > 0 
    ? duration / nonEmptyLines.length 
    : 0;
  const currentLineIdx = timePerLine > 0 
    ? Math.min(Math.floor(currentTime / timePerLine), nonEmptyLines.length - 1)
    : -1;
  
  let lineCounter = -1;
  
  return (
    <div className="max-h-[250px] overflow-y-auto bg-[#0a0a0a] border border-[#2a2a2a] rounded-lg p-4 space-y-1">
      {allLines.map((line, index) => {
        if (!line.trim()) {
          return <div key={index} className="h-2" />;
        }
        
        lineCounter++;
        const isCurrentLine = lineCounter === currentLineIdx;
        
        return (
          <div
            key={index}
            className={`px-3 py-1.5 rounded transition-all duration-200 font-mono text-sm ${
              isCurrentLine
                ? "bg-purple-600/30 text-white border-l-2 border-purple-500 scale-[1.02]"
                : "text-[#707070] hover:text-[#909090]"
            }`}
          >
            <span className="text-[#404040] text-xs mr-2 w-6 inline-block">
              {lineCounter + 1}
            </span>
            {line}
          </div>
        );
      })}
    </div>
  );
}

const STEPS = [
  { id: 1, label: "Audio", icon: Music },
  { id: 2, label: "Visuals", icon: Image },
  { id: 3, label: "Lyrics", icon: Type },
  { id: 4, label: "Generate", icon: Video },
];

const AI_LYRICS_COST = 400; // 400 credits = $4.00

export default function DeadWords() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [audioFile, setAudioFile] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [audioDuration, setAudioDuration] = useState(null);
  const [visuals, setVisuals] = useState([]);
  const [lyrics, setLyrics] = useState("");
  const [currentUser, setCurrentUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  
  // Audio player state
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);

  useEffect(() => {
    loadUser();
  }, []);

  // Create object URL when audio file changes
  useEffect(() => {
    if (audioFile) {
      const url = URL.createObjectURL(audioFile);
      setAudioUrl(url);
      return () => URL.revokeObjectURL(url);
    } else {
      setAudioUrl(null);
    }
  }, [audioFile]);

  // Audio player event handlers
  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
    }
  };

  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration);
    }
  };

  const togglePlayPause = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleSeek = (value) => {
    if (audioRef.current) {
      audioRef.current.currentTime = value[0];
      setCurrentTime(value[0]);
    }
  };

  const handleVolumeChange = (value) => {
    if (audioRef.current) {
      audioRef.current.volume = value[0];
      setVolume(value[0]);
    }
  };

  const skipTime = (seconds) => {
    if (audioRef.current) {
      audioRef.current.currentTime = Math.max(0, Math.min(duration, audioRef.current.currentTime + seconds));
    }
  };

  const formatTime = (time) => {
    const mins = Math.floor(time / 60);
    const secs = Math.floor(time % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const loadUser = async () => {
    try {
      const { data: { session }, error } = await supabase.auth.getSession();
      if (error || !session?.user) {
        setCurrentUser(null);
        setIsLoading(false);
        return;
      }

      const { data: profile } = await supabase
        .from("profiles")
        .select("*")
        .eq("id", session.user.id)
        .single();
        
      setCurrentUser(profile || {
        id: session.user.id,
        email: session.user.email,
        premium_credits: 0
      });
    } catch (err) {
      console.error("Error loading user:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleExtractLyrics = async (audioFile) => {
    if (!currentUser) {
      toast.error("Please log in to use AI lyrics extraction");
      navigate("/auth");
      return;
    }

    if ((currentUser.premium_credits || 0) < AI_LYRICS_COST) {
      toast.error(`You need ${AI_LYRICS_COST} credits ($4.00) for AI extraction. You have ${currentUser.premium_credits || 0}.`);
      return;
    }

    // Step 1: Deduct credits via Supabase
    const balanceBefore = currentUser.premium_credits || 0;
    const balanceAfter = balanceBefore - AI_LYRICS_COST;
    
    const { error: updateError } = await supabase
      .from("profiles")
      .update({ premium_credits: balanceAfter })
      .eq("id", currentUser.id);
    
    if (updateError) {
      throw new Error('Failed to deduct credits');
    }
    
    // Log the transaction
    await supabase.from("credit_transactions").insert({
      user_id: currentUser.id,
      transaction_type: 'dead_words_lyrics',
      amount: AI_LYRICS_COST / 100,
      credits_added: -AI_LYRICS_COST,
      balance_before: balanceBefore,
      balance_after: balanceAfter,
      metadata: { description: 'AI Lyrics Extraction - Dead Words' }
    });

    // Step 2: Call Edge Function for Music.AI lyrics extraction
    const { data: { session } } = await supabase.auth.getSession();
    
    const formData = new FormData();
    formData.append('audio', audioFile);
    formData.append('userId', currentUser.id);

    const { data, error } = await supabase.functions.invoke('extract-lyrics', {
      body: formData
    });

    if (error || !data) {
      toast.error("Extraction failed. Contact support for a refund.");
      throw new Error(error?.message || 'Failed to extract lyrics');
    }
    
    // Parse the lyrics JSON to extract plain text lines
    let plainLyrics = "";
    try {
      const lyricsData = typeof data.lyrics === 'string' ? JSON.parse(data.lyrics) : data.lyrics;
      if (Array.isArray(lyricsData)) {
        plainLyrics = lyricsData.map(line => line.text || '').join('\n');
      } else {
        plainLyrics = data.lyrics;
      }
    } catch (e) {
      // If parsing fails, use raw lyrics
      plainLyrics = data.lyrics;
    }
    
    // Update local user credits
    setCurrentUser(prev => ({
      ...prev,
      premium_credits: balanceAfter
    }));
    
    // Dispatch event for navbar credit update
    window.dispatchEvent(new CustomEvent('credits:updated'));
    
    toast.success("Lyrics extracted successfully!");
    return plainLyrics;
  };

  const canProceed = () => {
    switch (currentStep) {
      case 1:
        return audioFile !== null;
      case 2:
        return visuals.length > 0;
      case 3:
        return lyrics.trim().length > 0;
      default:
        return true;
    }
  };

  const handleNext = () => {
    if (currentStep < 4 && canProceed()) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  return (
    <div className="min-h-screen p-4 md:p-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Link 
            to="/tools" 
            className="p-2 rounded-lg hover:bg-[#1a1a1a] transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-[#808080]" />
          </Link>
          <img 
            src={deadWordsLogo} 
            alt="Dead Words" 
            className="w-12 h-12 object-contain"
          />
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-[#e0e0e0] font-holly-berry">
              Dead Words
            </h1>
            <p className="text-sm text-[#606060]">Lyric Video Generator</p>
          </div>
        </div>

        {/* Progress Steps */}
        <div className="flex items-center justify-between mb-8 px-4">
          {STEPS.map((step, index) => (
            <div key={step.id} className="flex items-center">
              <button
                onClick={() => step.id < currentStep && setCurrentStep(step.id)}
                className={`flex flex-col items-center gap-2 ${
                  step.id < currentStep ? 'cursor-pointer' : 'cursor-default'
                }`}
              >
                <div
                  className={`w-10 h-10 md:w-12 md:h-12 rounded-full flex items-center justify-center transition-all ${
                    step.id === currentStep
                      ? "bg-purple-600 text-white"
                      : step.id < currentStep
                      ? "bg-green-600 text-white"
                      : "bg-[#1a1a1a] text-[#606060] border border-[#2a2a2a]"
                  }`}
                >
                  {step.id < currentStep ? (
                    <Check className="w-5 h-5" />
                  ) : (
                    <step.icon className="w-5 h-5" />
                  )}
                </div>
                <span
                  className={`text-xs font-medium ${
                    step.id === currentStep
                      ? "text-purple-400"
                      : step.id < currentStep
                      ? "text-green-400"
                      : "text-[#606060]"
                  }`}
                >
                  {step.label}
                </span>
              </button>
              {index < STEPS.length - 1 && (
                <div
                  className={`hidden md:block w-16 lg:w-24 h-0.5 mx-2 ${
                    step.id < currentStep ? "bg-green-600" : "bg-[#2a2a2a]"
                  }`}
                />
              )}
            </div>
          ))}
        </div>

        {/* Credit Display */}
        {currentUser && (
          <div className="flex items-center justify-end gap-2 mb-4 text-sm">
            <Coins className="w-4 h-4 text-yellow-400" />
            <span className="text-[#808080]">
              Balance: <span className="text-yellow-400 font-medium">{currentUser.premium_credits || 0} credits</span>
            </span>
            <Link to="/shop" className="text-purple-400 hover:text-purple-300 ml-2">
              Buy more
            </Link>
          </div>
        )}

        {/* Step Content */}
        <div className="bg-[#0d0d0d] border border-[#1a1a1a] rounded-2xl p-6 md:p-8 mb-6">
          {currentStep === 1 && (
            <AudioUploader
              audioFile={audioFile}
              setAudioFile={setAudioFile}
              audioDuration={audioDuration}
              setAudioDuration={setAudioDuration}
            />
          )}
          {currentStep === 2 && (
            <VisualsUploader
              visuals={visuals}
              setVisuals={setVisuals}
            />
          )}
          {currentStep === 3 && (
            <LyricsEditor
              lyrics={lyrics}
              setLyrics={setLyrics}
              audioFile={audioFile}
              userCredits={currentUser?.premium_credits || 0}
              onExtractLyrics={handleExtractLyrics}
              aiCost={AI_LYRICS_COST}
            />
          )}
          {currentStep === 4 && (
            <VideoGenerator
              audioFile={audioFile}
              visuals={visuals}
              lyrics={lyrics}
            />
          )}
        </div>

        {/* Navigation Buttons */}
        {currentStep < 4 && (
          <div className="flex justify-between mb-8">
            <Button
              variant="outline"
              onClick={handleBack}
              disabled={currentStep === 1}
              className="border-[#2a2a2a] text-[#a0a0a0] hover:text-white"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <Button
              onClick={handleNext}
              disabled={!canProceed()}
              className="bg-purple-600 hover:bg-purple-700 text-white"
            >
              Continue
              <ArrowLeft className="w-4 h-4 ml-2 rotate-180" />
            </Button>
          </div>
        )}

        {/* Persistent Audio Player & Text Editor */}
        {(audioFile || lyrics) && (
          <div className="bg-[#0a0a0a] border border-[#1a1a1a] rounded-2xl p-4 md:p-6 mt-6 space-y-4">
            <h3 className="text-lg font-semibold text-[#e0e0e0] flex items-center gap-2">
              <Music className="w-5 h-5 text-purple-400" />
              Workstation
            </h3>

            {/* Audio Player */}
            {audioUrl && (
              <div className="bg-[#0d0d0d] rounded-xl p-4 border border-[#1a1a1a]">
                <audio
                  ref={audioRef}
                  src={audioUrl}
                  onTimeUpdate={handleTimeUpdate}
                  onLoadedMetadata={handleLoadedMetadata}
                  onEnded={() => setIsPlaying(false)}
                />
                
                <div className="flex items-center gap-4 mb-3">
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => skipTime(-10)}
                      className="p-2 rounded-lg hover:bg-[#1a1a1a] transition-colors text-[#808080] hover:text-white"
                    >
                      <SkipBack className="w-4 h-4" />
                    </button>
                    <button
                      onClick={togglePlayPause}
                      className="p-3 rounded-full bg-purple-600 hover:bg-purple-700 transition-colors text-white"
                    >
                      {isPlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5 ml-0.5" />}
                    </button>
                    <button
                      onClick={() => skipTime(10)}
                      className="p-2 rounded-lg hover:bg-[#1a1a1a] transition-colors text-[#808080] hover:text-white"
                    >
                      <SkipForward className="w-4 h-4" />
                    </button>
                  </div>
                  
                  <div className="flex-1">
                    <Slider
                      value={[currentTime]}
                      max={duration || 100}
                      step={0.1}
                      onValueChange={handleSeek}
                      className="cursor-pointer"
                    />
                    <div className="flex justify-between text-xs text-[#606060] mt-1">
                      <span>{formatTime(currentTime)}</span>
                      <span>{formatTime(duration)}</span>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2 w-28">
                    <Volume2 className="w-4 h-4 text-[#606060]" />
                    <Slider
                      value={[volume]}
                      max={1}
                      step={0.01}
                      onValueChange={handleVolumeChange}
                      className="cursor-pointer"
                    />
                  </div>
                </div>
                
                <div className="text-sm text-[#808080]">
                  <span className="text-purple-400">{audioFile?.name}</span>
                </div>
              </div>
            )}

            {/* Lyrics Display with Highlighting */}
            <div className="bg-[#0d0d0d] rounded-xl p-4 border border-[#1a1a1a]">
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-medium text-[#a0a0a0] flex items-center gap-2">
                  <Type className="w-4 h-4 text-purple-400" />
                  Lyrics Preview
                </label>
                <span className="text-xs text-[#606060]">
                  {lyrics.split('\n').filter(l => l.trim()).length} lines
                </span>
              </div>
              
              {/* Synced lyrics display */}
              {lyrics.trim() ? (
                <SyncedLyricsDisplay 
                  lyrics={lyrics} 
                  currentTime={currentTime} 
                  duration={duration} 
                />
              ) : (
                <div className="text-center py-8 text-[#505050] text-sm">
                  No lyrics yet - add them in Step 3
                </div>
              )}
              
              {/* Edit button */}
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentStep(3)}
                className="mt-3 w-full border-[#2a2a2a] text-[#808080] hover:text-white"
              >
                Edit Lyrics
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
